/**
 * V2 analytics — V1's event pipeline (analytics.js) rebuilt on the
 * store: page_view with UTM/referrer/device classification, navigation
 * with depth, session-correlated, batched (30s or 20 events) into
 * analytics_events via the raw gateway. Same row shape as V1:
 * { user_id, event_name, event_data, session_id, created_at }.
 */
import * as gateway from './gateway.js';

const FLUSH_MS = 30_000;
const MAX_QUEUE = 20;
const SESSION_KEY = 'yoink_session_id';
const LANDING_KEY = 'yoink_landing'; // localStorage, NOT session — see below

let storeRef = null;
let queue = [];
let timer = null;
let sessionId = null;
let depth = 0;
let lastView = null;
let landing = null;

function getSessionId() {
  if (sessionId) return sessionId;
  try {
    sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
  } catch {
    sessionId = `s-${Date.now()}`;
  }
  return sessionId;
}

/** V1's referrer/medium classifier, condensed. Exported for tests. */
export function classifyReferrer(referrer) {
  if (!referrer) return { source: 'direct', medium: 'none' };
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, '');
    if (host.includes(window.location.hostname.replace(/^www\./, ''))) {
      return { source: 'internal', medium: 'internal' };
    }
    if (/google\.|bing\.|duckduckgo\.|yahoo\./.test(host)) return { source: host, medium: 'organic' };
    if (/facebook\.|instagram\.|t\.co|twitter\.|x\.com|reddit\.|pinterest\./.test(host)) {
      return { source: host, medium: 'social' };
    }
    return { source: host, medium: 'referral' };
  } catch {
    return { source: 'direct', medium: 'none' };
  }
}

function deviceType() {
  const w = window.innerWidth || 0;
  if (w < 700) return 'phone';
  if (w < 1100) return 'tablet';
  return 'desktop';
}

/**
 * FIRST-TOUCH ATTRIBUTION (2026-07-25) — where this person originally came
 * from, remembered forever and stamped on every event.
 *
 * THE BUG THIS FIXES: source/medium lived only on the `page_view` event, and
 * page_view fires on the FIRST route change — before auth resolves, and for a
 * brand-new visitor before an account exists at all. track() reads
 * `storeRef.get().user?.id` at event time, so EVERY attribution-bearing row
 * was written with user_id = NULL. Measured 2026-07-25 against production:
 * 3,701 page_view rows, all carrying a source, ZERO carrying a user_id.
 *
 * The consequence was that "where did this signup come from?" had no answer.
 * Bridging page_view to a person via shared session_id recovered only 5 of 27
 * users (18%) — sessionStorage dies with the tab, so most sessions hold either
 * the page_view or the authenticated events, rarely both.
 *
 * The fix is to stop depending on one event surviving. Landing attribution is
 * captured once, kept in localStorage (survives tabs, sessions and the gap
 * between first visit and eventual signup), and attached to every event —
 * so any row that has a user_id also has the source.
 *
 * FIRST-touch, never overwritten: the question worth answering is what
 * originally brought someone in, not which internal link they last clicked.
 *
 * The existing `source`/`medium` keys on page_view are untouched — the admin
 * traffic panel aggregates on them (admin/traffic.js:205-206).
 */
function getLanding() {
  if (landing) return landing;
  try {
    const stored = localStorage.getItem(LANDING_KEY);
    if (stored) {
      landing = JSON.parse(stored);
      return landing;
    }
  } catch {
    // unreadable or non-JSON — fall through and re-derive
  }

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams('');
  }
  const referred = classifyReferrer(document.referrer);
  landing = {
    landing_source: params.get('utm_source') || referred.source,
    landing_medium: params.get('utm_medium') || referred.medium,
    landing_campaign: params.get('utm_campaign') || null,
  };

  // An internal referrer means this is a same-site navigation that happens to
  // be the first tracked hit — a page reload, or a link from the WordPress
  // front page. Recording "internal" as the origin would erase the real one,
  // and because this is write-once that error would be permanent. Leave it
  // unstored so the next genuine entry point gets the chance to claim it.
  if (landing.landing_medium === 'internal') return landing;

  try {
    localStorage.setItem(LANDING_KEY, JSON.stringify(landing));
  } catch {
    // private mode / quota — attribution degrades to per-visit, nothing breaks
  }
  return landing;
}

export function track(eventName, eventData = {}) {
  if (!storeRef) return;
  queue.push({
    user_id: storeRef.get().user?.id || null,
    event_name: eventName,
    // Landing attribution rides on EVERY event, so any row that has a user_id
    // also has a source. Spread first: an explicit key in eventData wins.
    event_data: { ...getLanding(), ...eventData },
    session_id: getSessionId(),
    created_at: new Date().toISOString(),
  });
  if (queue.length >= MAX_QUEUE) flush();
}

export async function flush() {
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  try {
    await gateway.rawInsert(gateway.TABLES.ANALYTICS_EVENTS, { body: batch });
  } catch {
    // Stale events aren't worth retrying (V1 behavior)
  }
}

/** Route-change hook: first change is the page_view, the rest navigation. */
export function trackRoute(routeName) {
  depth += 1;
  if (depth === 1) {
    const params = new URLSearchParams(window.location.search);
    track('page_view', {
      page: window.location.pathname,
      view: routeName,
      ...classifyReferrer(document.referrer),
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      device: deviceType(),
    });
  } else {
    track('navigation', { view: routeName, from: lastView, depth });
  }
  lastView = routeName;
}

export function initAnalytics(store) {
  storeRef = store;
  clearInterval(timer);
  timer = setInterval(flush, FLUSH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/** Test hook. */
export function _reset() {
  storeRef = null;
  queue = [];
  depth = 0;
  lastView = null;
  landing = null;  // memoized first-touch; a test setting a new referrer
  sessionId = null; // or storage state must get a fresh read, not the last one
  clearInterval(timer);
}

export function _queued() {
  return queue.slice();
}
